import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { RootState } from 'store';
import config from 'config/common';
import { FeatureFlags } from 'config/featureFlags.constants';
import { system_theme } from 'store/system/selectors';
import { user_userInfo } from 'store/user/selectors';
import { ThemeOptions } from 'interfaces/main';

export enum CustomNavOpenWith {
    IFRAME = 'iframe',
    DIALOG = 'dialogue',
    NEWTAB = 'newTab',
}

export interface CustomNavigationButtonConfig {
    isEnabled: boolean;
    targetUrl: string;
    openWith: CustomNavOpenWith;
    isLegacyMode: boolean;
}

const BP_DISABLED: CustomNavigationButtonConfig = {
    isEnabled: false,
    targetUrl: '',
    openWith: CustomNavOpenWith.DIALOG,
    isLegacyMode: false,
};

const OPEN_WITH_BY_NORMALIZED_VALUE: Record<string, CustomNavOpenWith> = {
    iframe: CustomNavOpenWith.IFRAME,
    newtab: CustomNavOpenWith.NEWTAB,
    dialogue: CustomNavOpenWith.DIALOG,
};

function normalizeOpenWith(value: string | undefined): CustomNavOpenWith {
    const normalized = value?.toLowerCase().replace(/[^a-z]/g, '') ?? '';
    return OPEN_WITH_BY_NORMALIZED_VALUE[normalized] ?? CustomNavOpenWith.DIALOG;
}

function applyUrlPlaceholders(url: string, lang: string, theme: string, crm_account_id: string): string {
    return url
        .replace(/\{lang\}/gi, lang || 'en')
        .replace(/\{theme\}/gi, theme || 'light')
        .replace(/\{crm_account_id\}/gi, crm_account_id || 'null');
}

export const useCustomNavigationButton = (): CustomNavigationButtonConfig => {
    const configEnv = useSelector((state: RootState) => state.config.env);
    const {
        i18n: { language },
    } = useTranslation();
    const theme: ThemeOptions = useSelector(system_theme);
    const crm_account_id = useSelector(user_userInfo)?.id ?? 'null';

    return useMemo(() => {
        // BP enablement:
        // - true -> use BP config
        // - false -> explicitly disabled by BP
        // - undefined -> fallback to legacy CP behavior
        const bpCustomNavEnabled =
            config.customNavigationButton.enabled ??
            configEnv?.REACT_APP_FEATURE_FLAGS_CUSTOM_NAVIGATION_BUTTON_IS_ENABLED;

        if (bpCustomNavEnabled === true) {
            const targetUrl =
                config.customNavigationButton.targetUrl ||
                configEnv?.REACT_APP_CUSTOM_NAVIGATION_BUTTON_TARGET_URL ||
                '';
            if (!targetUrl) return BP_DISABLED;

            const openWithRaw =
                config.customNavigationButton.openWith || configEnv?.REACT_APP_CUSTOM_NAVIGATION_BUTTON_OPEN_WITH;

            return {
                isEnabled: true,
                targetUrl: applyUrlPlaceholders(targetUrl, language, theme, crm_account_id),
                openWith: normalizeOpenWith(openWithRaw),
                isLegacyMode: false,
            };
        }

        if (bpCustomNavEnabled === false) {
            return BP_DISABLED;
        }

        const legacyUrl = config.tradersEducation?.url ?? '';
        const legacyEnabled = !!config.featuresFlags[FeatureFlags.TRADERS_EDUCATION] && !!legacyUrl;

        return {
            isEnabled: legacyEnabled,
            targetUrl: legacyUrl,
            openWith: config.tradersEducation?.openInNewTab ? CustomNavOpenWith.NEWTAB : CustomNavOpenWith.DIALOG,
            isLegacyMode: true,
        };
    }, [configEnv, language, theme, crm_account_id]);
};
